import { KeyboardEvent } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { AnalyticsEventPayload } from "@atlaskit/analytics-next/AnalyticsEvent";

import { Button, ButtonProps } from "@/components/adg4/price-card/src";
import TryIntent from "@/components/try-intent/src";
import { getEnvironment } from "@/utils/environment";

import { CloudProductKey } from "../constants";

import { useStickyCtaContext } from "./sticky-cta-context";

const ctaButtonStyles = css({
    font: "Charlie Text",
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "20px",
    height: "auto",
    textDecoration: "none",
    width: "fit-content",
    whiteSpace: "normal",
    paddingBlockStart: "8px",
    paddingBlockEnd: "8px"
});

export type CtaButtonProps = ButtonProps & {
    edition: string | undefined;
    dataProductKey: CloudProductKey;
    text: string;
    type: "primary" | "secondary";
    url: string;
    dataEventPropsLabel: string;
    isDisabled: boolean;
    containerId: string;
};

export const CtaButton = ({
    edition,
    dataProductKey,
    text,
    type,
    url,
    dataEventPropsLabel,
    containerId,
    isDisabled,
    ...props
}: CtaButtonProps) => {
    const stickyHeaderCtaEventHandlers = useStickyCtaEventHandlers({
        edition,
        containerId,
        productKey: dataProductKey
    });

    const envSpecificURL =
        getEnvironment(true) === "staging"
            ? url.replace(
                  /https:\/\/(www.)?atlassian.com/,
                  "https://wac.stg.internal.atlassian.com"
              )
            : url;

    const handleClick = () => {
        stickyHeaderCtaEventHandlers.onClick();
        // @ts-ignore
        props.onClick?.();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        stickyHeaderCtaEventHandlers.onKeyDown(e);
        // @ts-ignore
        props.onKeyDown?.(e);
    };

    return (
        <TryIntent
            intentType={"button"}
            containerId={containerId}
            productKey={dataProductKey}
            edition={edition}
        >
            <Button
                isDisabled={isDisabled}
                variant={type}
                color="black"
                href={envSpecificURL}
                dataEventProps={{
                    "data-product-key": dataProductKey,
                    "data-event-component": "linkButton",
                    "data-event-container": "priceBlock",
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }}
                css={ctaButtonStyles}
                {...props}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                {text}
            </Button>
        </TryIntent>
    );
};

interface UseStickyCtaEventHandlersProps {
    edition?: string;
    productKey: string;
    containerId: string;
}
export function useStickyCtaEventHandlers(
    props: UseStickyCtaEventHandlersProps
) {
    const { edition, productKey, containerId } = props;

    const { isSticky } = useStickyCtaContext();

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const payload: AnalyticsEventPayload = {
        eventType: "ui",
        action: "clicked",
        actionSubject: "linkButton",
        containerId,
        actionSubjectId: "pricingPlanCta",
        attributes: {
            productKey,
            edition,
            isSticky
        }
    };

    const onClick = () => {
        createAnalyticsEvent(payload).fire();
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== "Tab" && e.code !== "Enter" && e.code !== "Space") {
            createAnalyticsEvent(payload).fire();
        }
    };

    return {
        onClick,
        onKeyDown
    };
}
